import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ManagementArea } from '../data-models/area-models/management-area.model';
import { Subarea } from '../data-models/area-models/subarea.model';
import { LandingSite } from '../data-models/landing-site.model';
import { Trip } from '../data-models/trip-models/trip.model';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PrivacyHarvesterDialogComponent } from '../authentication/privacy-harvester-dialog/privacy-harvester-dialog.component';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import { environment } from '../../environments/environment';
import { sanitizeDecimal } from '../data-models/validator.model';

let SCREEN_WIDTH: string =  Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0).toString() + 'px';
let SCREEN_HEIGHT: string = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0).toString() + 'px';

@Injectable({
  providedIn: 'root'
})
export class SharedMethodsService {

  toastSuccess: string = $localize`:@@toastSuccess: Success`;
  toastWarning: string = $localize`:@@toastWarning: Warning`;
  toastErr: string = $localize`:@@toastErr: Error`;
  toastSignUpErr: string = $localize`:@@toastSignUpErr: Sign up failed`;
  toastLoginErr: string = $localize`:@@toastLoginErr: Login failed`;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private dialog: MatDialog
  ) {

  }


  /*  ---------- Area Methods  ---------- */


  // returns an array of the catch report's zones (if the zones parameter = true) or subareas
  public static separateSubareasAndZones(subareas: Subarea[], zones: boolean = false): Subarea[] {

    let result: Subarea[] = [];

    for (let subarea of subareas) { // separate subareas from zones
      if (subarea.zone && zones) { // if the subarea is a zone and we are asking for zones
        result.push(subarea);
      } else if (!subarea.zone && !zones) { // if the subarea is not a zone and we are not asking for zones
        result.push(subarea);
      }
    }

    return result;

  }

  // formats subareas or zones for the XML being sent to DFO
  public static formatSubareasOrZones(subareas: Subarea[], zones: boolean = false): string {

    let returnString: string = '';
    let subArray: Subarea[] = this.separateSubareasAndZones(subareas, zones); // separate subareas from zones

    if (subArray.length === 0) {
      return 'NA';
    }

    let counter: number = 1;
    for (let item of subArray) {
      if (!zones) { // if we want subareas
        returnString += (item.managementAreaNames[0] + '-' + item.name); // format subarea appropriately
      } else if (zones) { // if we want zones
        returnString += (item.name); // format zone appropriately
      }
      if (counter < subArray.length) {
        returnString += ','; // add a comma if it is not the last array item
      }
      counter++;
    }

    return returnString;

  }


  // returns all subareas or zones within the specified management area
  getSubareas(managementAreaName: string, zone: boolean, allSubareas: Subarea[]): Subarea[] {

    let subareas: Subarea[] = [];

    for (let area of allSubareas) {
      if (area.managementAreaNames.includes(managementAreaName)) {
        if ((zone) || (!area.zone && !zone)) {
          subareas.push(area);
        }
      }
    }
    return subareas;

  }

  // returns the index of the matching management area (used to patch the form values)
  getLastManagementArea(area: ManagementArea, managementAreas: ManagementArea[]): number {

    let index: number = 0;
    for (let currArea of managementAreas) {
      if (currArea.ID === area.ID) {
        return index;
      }
      index++;
    }
    return index;

  }

  // returns all management areas within the specified license area
  getManagementAreas(licenseAreaName: string, allManagementAreas: ManagementArea[]): ManagementArea[] {
    return allManagementAreas.filter((area: ManagementArea) => area.licenseAreaNames.includes(licenseAreaName));
  }


  /*  ---------- Forbid Form Methods  ---------- */


  // prevents only whitespace from being added to a form field (a mix of characters and whitespace is allowed)
  forbidWhitespaceOnly(controlName: string, form: UntypedFormGroup): void {

    let control: AbstractControl = form.get(controlName)!;
    const regex: RegExp = /^\S.*$/; // matches any input that includes non-whitespace characters
    if (!regex.test(control.value)) {
      control.setValue('');
    }

  }

  // forbid field from accepting anything but digits (strips decimal points too - integers only)
  forbidNonNumeric(controlName: string, form: UntypedFormGroup): void {

    let control: AbstractControl = form.get(controlName)!;
    control.setValue(control.value.replace(/[^0-9]/g, ''));

  }

  // forbid field from accepting anything but a well-formed decimal number: strips non-digits,
  // keeps only the first decimal point, and truncates to maxDecimals digits after it
  forbidNonDecimal(controlName: string, form: UntypedFormGroup, maxDecimals: number = 1): void {

    let control: AbstractControl = form.get(controlName)!;
    control.setValue(sanitizeDecimal(control.value, maxDecimals), { emitEvent: false });

  }


  /*  ---------- Date Methods  ---------- */

  // reloads the current window
  reloadWindow(): void {

    window.location.reload();

  }

  // returns the trip's start date with no defined time
  getMinDate(currTrip: Trip): Date {

    let minDate: Date = new Date(currTrip.getStartDate());
    minDate.setHours(0, 0, 0, 0);
    return minDate;

  }

  // returns whicherver date is larger/latest: the trip's start date or the date provided in the parameter
  getCalendarDate(currTrip: Trip, dateIn: Date): Date {

    if (dateIn < currTrip.getStartDate()) {
      let dateOut: Date = new Date(currTrip.getStartDate());
      dateOut.setHours(0, 0, 0, 0);
      return dateOut;
    } else {
      return dateIn;
    }

  }

  getDate(date: Date): Date {

    return new Date(date);

  }

  // adds the provided time (hours and minutes) to a given date object
  setDateTime(date: Date, time: { hour: number, minute: number }): void {

    // Check if startHours and startMinutes are defined
    let startHours: number = (time.hour !== undefined && time.hour !== null) ? time.hour : date.getHours();
    let startMinutes: number = (time.minute !== undefined && time.minute !== null) ? time.minute : date.getMinutes();

    date.setHours(startHours);
    date.setMinutes(startMinutes);
    date.setSeconds(0);
    date.setMilliseconds(0);
    
  }


  /*  ---------- Toastr Message Display Methods --------- */

  toastUnableToSubmitReport(): void {

    const message: string = $localize`:@@msgUnabletoSubmitReporttoDFO: Unable to Submit Report to DFO`;
    this.toastr.error(message, this.toastErr);

  }

  toastNoCatchMatch(): void {

    const message: string = $localize`:@@msgNoCatchMatchGivenCriteria: No Catch Matched The Given Criteria`;
    this.toastr.error(message, this.toastErr);

  }

  toastInvalidForm(): void {

    const message: string = $localize`:@@msgNotAllRequiredFieldsFilled: Not all required fields filled`;
    const toastInvalid: string = $localize`:@@InvalidForm: Invalid Form`;
    this.toastr.error(message, toastInvalid);

  }

  toastFutureDateTime(): void {

    const message: string = $localize`:@@msgFutureDateTime: The date and time cannot be in the future`;
    const toastInvalid: string = $localize`:@@InvalidForm: Invalid Form`;
    this.toastr.error(message, toastInvalid);

  }

  toastEndDateBeforeReports(): void {

    const message: string = $localize`:@@msgEndDateBeforeReports: The end date cannot be before your last catch or pause report`;
    const toastInvalid: string = $localize`:@@InvalidForm: Invalid Form`;
    this.toastr.error(message, toastInvalid);

  }

  toastPasswordsNotMatching(): void {

    const message: string = $localize`:@@msgPasswordsNotMatching: Passwords must be matching`;
    const toastInvalid: string = $localize`:@@InvalidForm: Invalid Form`;
    this.toastr.error(message, toastInvalid);

  }

  toastSubmitAnyReportsFailed(): void {

    const message: string = $localize`:@@msgCouldNotSubmitAnyReports: Could not submit any outstanding reports.`;
    this.toastr.error(message, this.toastErr);

  }

  toastSubmitAllReportsFailed(): void {

    const message: string = $localize`:@@msgCouldNotSubmitAllReports: Could not submit all outstanding reports.`;
    this.toastr.error(message, this.toastErr);

  }

  toastUnableToCheckNotices(): void {

    const message: string = $localize`:@@msgUnableToCheckNoticesWhileOffline: Unable to check for notices while offline`;
    this.toastr.error(message, this.toastErr);

  }

  toastUnableToRetrieveNotices(): void {

    const message: string = $localize`:@@msgCouldNotRetrieveNotices: Could not retrieve notices`;
    this.toastr.error(message, this.toastErr);

  }

  toastSomethingWentWrong(): void {

    const message: string = $localize`:@@msgSomethingWentWrong: Something went wrong`;
    this.toastr.error(message, this.toastErr);

  }

  toastMissingInformation(): void {

    const message: string = $localize`:@@msgMissingInformation: Missing information`;
    this.toastr.error(message, this.toastErr);

  }

  toastNetUniqueError(): void {

    const message: string = $localize`:@@msgNetNotUnique: You already have a net with this name and type.`;
    this.toastr.error(message, this.toastErr);

  }

  toastLogbookUniqueError(): void {

    const message: string = $localize`:@@msgLogbookNotUnique: You already have a logbook with this number.`;
    this.toastr.error(message, this.toastErr);

  }

  toastVRNUniqueError(): void {

    const message: string = $localize`:@@msgVRNNotUnique: You already have a vessel with this VRN.`;
    this.toastr.error(message, this.toastErr);

  }

  toastFINUniqueError(): void {

    const message: string = $localize`:@@msgFINNotUnique: You already have a fisher with this FIN.`;
    this.toastr.error(message, this.toastErr);

  }

  toastDataSyncError(): void {

    const message: string = $localize`:@@msgCouldNotSyncData: Could not sync data`;
    this.toastr.error(message, this.toastErr);

  }

  toastSignupMissingInfoError(): void {

    const message: string = $localize`:@@msgMissingInformation: Missing information`;
    this.toastr.error(message, this.toastSignUpErr);

  }

  toastOfflineError(): void {

    const message: string = $localize`:@@msgCannotSignUpOffline: Must be online to complete request`;
    this.toastr.error(message, this.toastSignUpErr);

  }

  toastOfflineSyncError(): void {

    const message: string = $localize`:@@msgCannotSignUpOffline: Must be online to complete request`;
    this.toastr.error(message, this.toastErr);

  }

  toastSignupCouldNotCompleteError(): void {

    const message: string = $localize`:@@msgSignUpCouldNotComplete: Sign up could not be completed`;
    this.toastr.error(message, this.toastSignUpErr);

  }

  toastSignupEmailInUseError(): void {

    const message: string = $localize`:@@msgEmailAlreadyInUse: Email already in use`;
    this.toastr.error(message, this.toastSignUpErr);

  }

  toastLoginUserNotFoundError(): void {

    const message: string = $localize`:@@msgUserNotFound: User not found`;
    this.toastr.error(message, this.toastLoginErr);

  }

  toastLoginIncorrectEmailOrPasswordError(): void {

    const message: string = $localize`:@@msgEmailPasswordIncorrect: Email or password incorrect`;
    this.toastr.error(message, this.toastLoginErr);

  }

  toastLoginUserEmailRequiredError(): void {

    const message: string = $localize`:@@msgUserEmailRequired: User email required`;
    this.toastr.error(message, this.toastLoginErr);

  }

  toastLoginMissingError(): void {

    const message: string = $localize`:@@msgMissing: Missing Information`;
    this.toastr.error(message, this.toastLoginErr);

  }

  toastLoginUserEmailNotDefined(): void {

    const message: string = $localize`:@@msgPleaseAuthenticateAgain: Please authenticate again`;
    const toastIDErr: string = $localize`:@@toastUserIDOrEmailNotDefined: User ID or Email not defined`;
    this.toastr.error(message, toastIDErr);

  }

  toastUserNotStoredOnDeviceError(): void {

    const message: string = $localize`:@@msgUserNotStoredOnDevice: User not stored on device`;
    this.toastr.error(message, this.toastErr);

  }

  toastDataRetrievalError(errorMsg: string): void {

    const message: string = $localize`:@@DataRetrievalError: Data Retrieval Error`;
    if (errorMsg === "Username not saved to storage") {
      const toastErr: string = $localize`:@@UsernameNotSavedToStorage: Username not saved To storage`;
      this.toastr.error(message, toastErr);
    } else if (errorMsg === "Not authenticated") {
      const toastErr: string = $localize`:@@NotAuthenticated: Not authenticated`;
      this.toastr.error(message, toastErr);
    } else if (errorMsg === "User ID or Email not defined") {
      const toastErr: string = $localize`:@@UserIDorEmailNotDefined: User ID or Email not defined`;
      this.toastr.error(message, toastErr);
    } else {
      const toastErr: string = $localize`:@@UnknownError: Unknown Error`;
      this.toastr.error(message, toastErr);
    }

  }

  toastNoCatch(isOffload: boolean = false): void {

    const message: string = $localize`:@@msgNoCatchEntered: No Catch Entered`;
    const toastWarning: string = $localize`:@@toastWarning: Warning`;
    if(isOffload) this.toastr.error(message, toastWarning);
    else this.toastr.warning(message, toastWarning);

  }

  toastEmailSent(): void {

    const message: string = $localize`:@@msgEmailSent!: Email Sent!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastReportsSubmitted(): void {

    const message: string = $localize`:@@msgAllReportsSubmitted!: All outstanding reports successfully submitted!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastNetAdded(): void {

    const message: string = $localize`:@@msgNetAdded!: Net Added!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastNetCannotDelete(): void {

    const message: string = $localize`:@@msgNetCannotDelete!: Net is used on one or more current reports. Cannot delete.`;
    this.toastr.warning(message, this.toastWarning);

  }

  toastNetCannotEdit(): void {

    const message: string = $localize`:@@msgNetCannotEdit!: Net is used on one or more current reports. Cannot edit.`;
    this.toastr.warning(message, this.toastWarning);

  }

  toastNetUpdated(): void {

    const message: string = $localize`:@@msgNetUpdated!: Net Updated!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastLogbookAdded(): void {

    const message: string = $localize`:@@msgLogbookAdded!: Logbook Added!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastVesselAdded(): void {

    const message: string = $localize`:@@msgVesselAdded!: Vessel Added!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastVesselUpdated(): void {

    const message: string = $localize`:@@msgVesselUpdated!: Vessel Updated!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastVesselCannotDelete(): void {

    const message: string = $localize`:@@msgVesselCannotDelete!: Vessel is registered to a logbook or used on the current trip. Cannot delete.`;
    this.toastr.warning(message, this.toastWarning);

  }

  toastProfileUpdated(): void {

    const message: string = $localize`:@@msgProfileInformationUpdated!: Profile Information Updated!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastNewNoticesReceived(): void {

    const message: string = $localize`:@@msgNewNoticesReceived: New Notices Received!`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastSignUpComplete(): void {

    const message: string = $localize`:@@msgSignUpComplete: Sign up complete`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastPasswordChanged(): void {

    const message: string = $localize`:@@msgPasswordChanged: Password Changed`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastSavedSuccessfully(): void {

    const message: string = $localize`:@@msgSaveSuccessful: Saved Report`;
    this.toastr.success(message, this.toastSuccess);

  }

  toastGeolocationDisabled(): void {
    const message: string = $localize`:@@msgGeolocationDisabled: Allow geolocation permission to access device location`;
    this.toastr.warning(message, this.toastWarning);
  }

  toastGeolocationDisabledError(): void {
    const message: string = $localize`:@@msgGeolocationDisabled: Allow geolocation permission to access device location`;
    this.toastr.warning(message, this.toastErr);
  }

  toastTripDataSynced(): void {
    const message: string = $localize`:@@msgTripDataSynced: Trip data synced`;
    this.toastr.success(message, this.toastSuccess);
  }

  toastSessionExpired(): void {
    const message: string = $localize`:@@msgSessionExpired: Sync failed. Check your connection or try again`;
    this.toastr.error(message, this.toastWarning);
  }

  toastLogbookAdminSuccess(message: string) {
    this.toastr.success(message, this.toastSuccess);
  }


  /*  ---------- Misc Methods  ---------- */


  //Determines the status of the report based on the confirmation number content
  public static getReportStatus(confirmationNumber: string): string {

    confirmationNumber = (confirmationNumber ?? "").trim();

    if (confirmationNumber.substring(0, 3) == "FOS") return "Accepted";
    else if (confirmationNumber == "Incomplete") return confirmationNumber;
    else if (confirmationNumber == "Not submitted") return confirmationNumber;
    else if (confirmationNumber.includes("Awaiting response")) return "Awaiting response";
    else return "Error with submission";

  }

  // local start-of-day copy. Never mutates the input: callers pass dates that are aliased into
  // live report objects (e.g. a catch report component's catchDate IS dailyCatchReports[i].dateFished),
  // so an in-place setHours would rewrite persisted report data. Tolerates the ISO strings that
  // reports can carry back from PouchDB.
  public static toStartOfDay(date: Date | string): Date {

    let day: Date = new Date(date);
    day.setHours(0, 0, 0, 0);
    return day;

  }

  // whether two dates fall on the same calendar day in local time. Deliberately not a UTC-day
  // comparison: report dates range from local midnight (a fresh calendar pick) through the trip's
  // start clock time to local noon (submitted), and a 23:xx local date lands on the next UTC day.
  public static isSameLocalDay(date1: Date | string, date2: Date | string): boolean {

    return SharedMethodsService.toStartOfDay(date1).getTime() === SharedMethodsService.toStartOfDay(date2).getTime();

  }

  // whether the trip already holds a DFO-confirmed (FOS…) catch or pause report on the given calendar
  // day, ignoring the report currently being filled out. FOS accepts one catch/pause report per day and
  // rejects both types if either already exists for that day, so callers warn the user before submitting.
  public static hasConfirmedReportOnDate(trip: Trip, date: Date | string, excludeInternalId: string = ''): boolean {

    const isConfirmed = (confirmationNumber: string) =>
      SharedMethodsService.getReportStatus(confirmationNumber) === 'Accepted';

    // report types are inferred rather than imported: the report models import SharedMethodsService
    // for generateReportID, so importing them back here would add a value-level circular dependency
    return trip.dailyCatchReports.some(report => report.internalId !== excludeInternalId
        && isConfirmed(report.confirmationNumber) && SharedMethodsService.isSameLocalDay(report.dateFished, date))
      || trip.pauseFishingReports.some(report => report.internalId !== excludeInternalId
        && isConfirmed(report.confirmationNumber) && SharedMethodsService.isSameLocalDay(report.pauseDate, date));

  }

  // the earliest day of the trip with no DFO-confirmed catch or pause report, clamped to today.
  public static getFirstUnreportedDay(trip: Trip): Date {

    let today: Date = SharedMethodsService.toStartOfDay(new Date());
    let day: Date = SharedMethodsService.toStartOfDay(trip.getStartDate());

    while (day.getTime() <= today.getTime()) {
      if (!SharedMethodsService.hasConfirmedReportOnDate(trip, day)) return day;
      day.setDate(day.getDate() + 1);
    }

    return today;

  }

  //
  static generateReportID(reportType: string): string {

    let id: number = Math.floor(Math.random() * 900000) + 100000;
    let reportID: string = reportType + id.toString();

    return reportID;

  }

  // calls backend function to add the tripID into the lookup database
  public saveToLookupDictionary(userEmail: String, tripID: String): Observable<any> {

    userEmail = userEmail.replace(/\+/g, "%2b");
    const url = `${environment.addtoDictionaryDb}&username=${userEmail}&tripID=${tripID}`;
    return this.http.get<any>(url);

  }


  // causes the program to 'wait' for the specified number of milliseconds (ms)
  public static delay(ms: number): Promise<unknown> {

    return new Promise(resolve => setTimeout(resolve, ms));

  }

  // returns all landing sites that are within the specified license area
  getLandingSites(licenseAreaNames: string, landingSites: LandingSite[]): LandingSite[] {

    let relevantSites: LandingSite[] = [];
    let siteCodes: string[] = []; // used to prevent duplicates (data from Couch DB may contain duplicate landing sites)

    for (let site of landingSites) {
      if (site.licenseAreaNames.includes(licenseAreaNames) && !siteCodes.includes(site.code)) {
        relevantSites.push(site);
        siteCodes.push(site.code);
      }
    }
    
    return relevantSites.sort((s1: LandingSite, s2: LandingSite) => 0 - (s1.name < s2.name ? 1 : -1));

  }

  // go back to the user's current home screen
  toHome(currHomepage: string): void {

    this.router.navigate([currHomepage]);

  }

  // return a generic list of strings
  public static getStringList(list: any): string[] {

    // if the string list does not exist, return an empty array
    if (!list) {
      return [];
    }

    let stringList: string[] = [];
    for (let item of list) {
      stringList.push(String(item));
    }
    return stringList;

  }



  /*  ---------- Privacy Notice / Harvester Attestation Methods  ---------- */

  openNoticeDialog() {
    return this.dialog.open(PrivacyHarvesterDialogComponent, {
      height: SCREEN_HEIGHT,
      width: SCREEN_WIDTH,
      disableClose: true,
      closeOnNavigation: false
    }).afterClosed()
  }

  openSkipPrivacyConfirmationDialog() {
    return this.dialog.open(ConfirmationDialogComponent, {
      data: {screen: 'privacyConfirm'}
    }).afterClosed()
  }

}

